/**
 * Note: this file contains a bunch of indinividual pieces of the commerce header bar.
 * These pieces are assembled together in the CommerceHeader component below.
 *
 * But on mobile, we don't use the CommerceHeader component,
 * because the commerce header is actually part of the Sheet component's title/description etc.
 * That's why all these individual pieces are exported on their own:
 * so they can be used within the CommerceMobileSheet component.
 */

import {
  Cart,
  ChevronLeft,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import { Button } from "client/designSystem/Button"

import { useCartContext } from "../cart/CartContext"
import { CommerceView, useShopContext } from "../ShopContext"

import styles from "./CommerceHeader.module.css"

export const CommerceHeaderBackButton = () => {
  const {
    shop,
    view,
    setView,
    currentlySelectedProductId,
    setCurrentlySelectedProductId,
    setCurrentlySelectedVariantId,
    showImageGallery,
    setShowImageGallery,
    setAddedProduct,
  } = useShopContext()

  const isOnCartScreen = view === CommerceView.CART

  const showBackButtonFromProductDetails =
    view === CommerceView.PRODUCT &&
    currentlySelectedProductId &&
    !showImageGallery
  const showBackButtonFromImageGallery =
    view === CommerceView.PRODUCT && showImageGallery
  const showBackButtonFromCart = isOnCartScreen && !!shop.activeSale

  const showBackButton =
    showBackButtonFromProductDetails ||
    showBackButtonFromImageGallery ||
    showBackButtonFromCart

  if (!showBackButton) {
    return null
  }
  return (
    <Button
      variant="tertiary-gray"
      size="md"
      icon={ChevronLeft}
      iconOnly
      onClick={() => {
        if (showBackButtonFromProductDetails) {
          setAddedProduct(null)
          setCurrentlySelectedProductId(null)
          setCurrentlySelectedVariantId(null)
          return
        }
        if (showBackButtonFromImageGallery) {
          setShowImageGallery(false)
          return
        }
        if (showBackButtonFromCart) {
          setView(CommerceView.PRODUCT)
        }
      }}
      data-robot="commerce-mobile-back"
    />
  )
}

export const useCommerceHeaderTitle = () => {
  const { view, currentlySelectedProductId } = useShopContext()
  if (
    view === CommerceView.PRODUCT &&
    currentlySelectedProductId === null
  ) {
    return "Shop products"
  }
  if (
    view === CommerceView.PRODUCT &&
    currentlySelectedProductId !== null
  ) {
    return "Product details"
  }
  if (view === CommerceView.CART) {
    return "Your cart"
  }
  return ""
}

export const useCommerceHeaderDescription = () => {
  const { view, currentlySelectedProductId, shopDetails } =
    useShopContext()
  const { cart } = useCartContext()

  if (
    view === CommerceView.PRODUCT &&
    currentlySelectedProductId === null &&
    shopDetails
  ) {
    return shopDetails.name
  }
  if (view === CommerceView.CART && cart) {
    return `${cart.totalQuantity} ${
      cart.totalQuantity === 1 ? "item" : "items"
    }`
  }
  return ""
}

export const CommerceHeaderCartButton = () => {
  const { view, setView, setAddedProduct } = useShopContext()
  const { cart } = useCartContext()
  if (view === CommerceView.CART || !cart || cart.totalQuantity < 1) {
    return null
  }
  return (
    <Button
      variant="secondary"
      size="md"
      icon={Cart}
      notificationDotText={cart.totalQuantity.toString()}
      onClick={() => {
        setView(CommerceView.CART)
        setAddedProduct(null)
      }}
      data-robot="commerce-mobile-cart-open"
    >
      Cart
    </Button>
  )
}

export const CommerceHeader = () => {
  const title = useCommerceHeaderTitle()
  const description = useCommerceHeaderDescription()
  return (
    <div className={styles.commerceHeader}>
      <div className={styles.commerceHeaderLeftSection}>
        <CommerceHeaderBackButton />
      </div>
      <div className={styles.commerceHeaderTitleSection}>
        {title && <h2 className="title-md text-color-primary">{title}</h2>}
        {description && (
          <p className="body-md text-color-secondary-subdued">
            {description}
          </p>
        )}
      </div>
      <div className={styles.commerceHeaderRightSection}>
        <CommerceHeaderCartButton />
      </div>
    </div>
  )
}
